/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, TextField } from "@mui/material";
import LabelledField from "components/labelled-field";
import { ComponentCardsItemContext } from "./component-cards-context";
import { CardsImage } from "./cards-image";
import { CardsLink } from "./cards-link";
import { DateTimeEdit } from "cms/fields/layout/controls/date-time";
import { isRichText } from "cms/rich-text/richtext-utils";
import { Logger } from "cms/utils/log/logger";

const CardsItemEditor = () => {
    const { t } = useTranslation();
    const componentCardsItemContext = useContext(ComponentCardsItemContext);
    const index = componentCardsItemContext?.index;
    const position = componentCardsItemContext?.position;
    const item = componentCardsItemContext?.item;
    const updateItemByPosition = componentCardsItemContext?.updateItemByPosition;

    const _image = item?.image;
    const _heading = item?.heading;
    const _date = item?.date;
    const _link = item?.link;
    let _description = item?.description;

    if (isRichText(item?.description)) {
        Logger.warn("Description contained RichText. should be text");
        _description = "";
    }

    const [image, setImage] = useState(_image);
    const [heading, setHeading] = useState(_heading);
    const [date, setDate] = useState(_date);
    const [link, setLink] = useState(_link);
    const [description, setDescription] = useState(_description);

    const updateImage = (guid) => {
        setImage(guid);
    };

    const onChangeHeading = (event) => {
        const newHeading = event.target.value;
        setHeading(newHeading);
    };

    const updateLink = async (newLink) => {
        setLink(newLink);
    };

    const onChangeDateTime = async (date) => {
        setDate(date);
    };

    const onChangeDescription = (event) => {
        const newDescription = event.target.value;
        setDescription(newDescription);
    };
    useEffect(() => {
        const nextItem = structuredClone({
            ...item,
            image,
            heading,
            date,
            description,
            link,
        });
        updateItemByPosition(nextItem, position);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [description, link, heading, image, date]);

    return (
        <div className="simplicity-image-list-item"
            style={{ width: "100%" }}
        >
            <Stack
                className="simplicity-image-list-item-container"
                direction="row"
                justifyContent="flex-start"
            >
                <div className="simplicity-image-list-item-image-wrapper">
                    <div className="simplicity-image-card-image">
                        <CardsImage index={index} image={image} updateImage={updateImage} />
                    </div>
                    <div className="simplicity-image-list-item-heading">
                        <LabelledField label="Heading">
                            <TextField
                                onChange={onChangeHeading}
                                id="heading"
                                defaultValue={heading}
                                fullWidth={true}
                            />
                        </LabelledField>
                        <LabelledField label="Date (Optional)">
                            <DateTimeEdit value={date} onChangeDateTime={onChangeDateTime} type="field.date"/>
                        </LabelledField>
                    </div>
                </div>
                <div className="simplicity-image-list-item-detail-wrapper">
                    <Box>
                        <LabelledField label={`${t("commons.description")}:`}>
                            <TextField
                                onChange={onChangeDescription}
                                id="description"
                                multiline
                                defaultValue={description}
                                fullWidth={true}
                            />
                        </LabelledField>
                        <div className="description-length">{description?.length || 0}/180 characters</div>
                    </Box>
                    <Box>
                        <LabelledField label={`${t("commons.link")}: `}>
                            <CardsLink index={index} link={link} updateLink={updateLink} />
                        </LabelledField>
                    </Box>
                </div>
            </Stack>
        </div>
    );
};
CardsItemEditor.displayName = "CardsItemEditor";
export { CardsItemEditor };
